import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { SEO } from './SEO';

const WIZARD_TITLES: Record<string, string> = {
  search: 'Find your company — Dormant Accounts Filing',
  confirm: 'Confirm figures — Dormant Accounts Filing',
  auth: 'Enter authentication code — Dormant Accounts Filing',
  pay: 'Payment — Dormant Accounts Filing',
  success: 'Filing submitted — Dormant Accounts Filing',
};

const STEPS = [
  { key: 'search', label: 'Company Lookup' },
  { key: 'confirm', label: 'Confirmation' },
  { key: 'auth', label: 'Authentication' },
  { key: 'pay', label: 'Payment' },
];

export function WizardLayout({
  step,
  children,
}: {
  step: 'search' | 'confirm' | 'auth' | 'pay' | 'success';
  children: ReactNode;
}) {
  const activeIdx = STEPS.findIndex((s) => s.key === step);
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title={WIZARD_TITLES[step] ?? 'Dormant Accounts Filing'} noindex />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/" aria-label="Dormant Accounts Filing – home">
            <Logo />
          </Link>
          {/* Decorative tagline – hide on phones so the brand has full width. */}
          <span className="hidden whitespace-nowrap text-sm text-slate-500 md:inline">
            £20 · filed direct to Companies House
          </span>
        </div>
      </header>

      {/* On phones the four step labels don't all fit. Show just the active
          step + a "Step N of 4" pip; show the full strip from sm: up. */}
      <nav className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex items-center justify-between px-4 py-3 sm:hidden">
          <span className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-[11px] font-semibold text-white">
              {Math.max(1, activeIdx + 1)}
            </span>
            <span className="text-sm font-medium text-brand-700">
              {STEPS[activeIdx]?.label || 'Done'}
            </span>
          </span>
          <span className="text-xs text-slate-400">
            Step {Math.max(1, activeIdx + 1)} of {STEPS.length}
          </span>
        </div>
        <ol className="mx-auto hidden max-w-5xl gap-2 px-6 py-3 text-sm sm:flex">
          {STEPS.map((s, i) => {
            const done = i < activeIdx || step === 'success';
            const active = i === activeIdx;
            return (
              <li
                key={s.key}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 ${
                  active
                    ? 'bg-brand-50 text-brand-700 font-medium'
                    : done
                      ? 'text-slate-500'
                      : 'text-slate-400'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                    active
                      ? 'bg-brand-600 text-white'
                      : done
                        ? 'bg-slate-300 text-white'
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {i + 1}
                </span>
                {s.label}
              </li>
            );
          })}
        </ol>
      </nav>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-10">{children}</main>
    </div>
  );
}
